<script setup>
  const localePath = useLocalePath()
  const consentStore = useConsentStore()
  const { menuMain } = useMainMenu()
</script>

<template>
  <footer
    class="relative overflow-hidden bg-gradient-to-br from-sky-950 via-indigo-950 to-slate-950 text-white py-8 px-4"
  >
    <!-- Animated background blobs -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
      <div
        class="absolute top-1/4 right-1/4 w-96 h-96 bg-sky-400 rounded-full mix-blend-multiply filter blur-3xl animate-blob"
      ></div>
      <div
        class="absolute bottom-1/4 left-1/4 w-96 h-96 bg-indigo-400 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"
      ></div>
      <div
        class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"
      ></div>
    </div>

    <!-- Decorative grid pattern -->
    <div class="absolute inset-0 opacity-5">
      <div
        class="absolute inset-0"
        style="
          background-image: radial-gradient(circle at 1px 1px, rgb(255 255 255 / 0.15) 1px, transparent 0);
          background-size: 40px 40px;
        "
      ></div>
    </div>

    <div class="container mx-auto relative z-10">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-12">
        <!-- Company Info -->
        <div class="space-y-4">
          <div class="group">
            <picture>
              <source srcset="/logo/logo_UIA_transparent.webp" type="image/webp" />
              <img src="/logo/logo_UIA_transparent.png" alt="UIA Logo" class="" />
            </picture>
          </div>
          <div class="space-y-2">
            <p class="text-sm text-slate-300">{{ $t('footer.adress') }}</p>
            <p class="text-sm text-slate-300">{{ $t('footer.country') }}</p>

            <div class="pt-1 space-y-1.5">
              <a
                href="tel:+33144885566"
                class="group flex items-center space-x-2 text-sm text-slate-300 hover:text-sky-300 transition-all duration-300"
              >
                <div
                  class="flex items-center justify-center w-7 h-7 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 group-hover:bg-white/10 group-hover:border-sky-400/30 transition-all duration-300"
                >
                  <font-awesome class="fa-fw text-xs" :icon="['fal', 'phone']" />
                </div>
                <span>{{ $t('footer.phone') }}</span>
              </a>
              <NuxtLink
                :to="localePath({ name: 'contact' })"
                class="group flex items-center space-x-2 text-sm text-slate-300 hover:text-sky-300 transition-all duration-300"
              >
                <div
                  class="flex items-center justify-center w-7 h-7 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 group-hover:bg-white/10 group-hover:border-sky-400/30 transition-all duration-300"
                >
                  <font-awesome class="fa-fw text-xs" :icon="['fal', 'envelope']" />
                </div>
                <span>{{ $t('footer.contact') }}</span>
              </NuxtLink>
            </div>
          </div>
        </div>

        <!-- Social & Partner -->
        <div class="space-y-4">
          <div class="space-y-3 mt-12">
            <h4 class="text-base font-semibold text-white">{{ $t('footer.social') }}</h4>
            <div class="flex flex-wrap gap-2">
              <a
                href="https://www.linkedin.com/company/union-internationale-des-avocats/"
                target="_blank"
                rel="noopener noreferrer"
                class="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-sky-400/30 hover:scale-110 transition-all duration-300"
                aria-label="LinkedIn"
              >
                <font-awesome
                  class="fa-fw text-slate-300 group-hover:text-sky-300 transition-colors"
                  :icon="['fab', 'linkedin-in']"
                />
              </a>
              <a
                href="https://www.instagram.com/unionintavocats/"
                class="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-sky-400/30 hover:scale-110 transition-all duration-300"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <font-awesome
                  class="fa-fw text-slate-300 group-hover:text-sky-300 transition-colors"
                  :icon="['fab', 'instagram']"
                />
              </a>
              <a
                href="https://www.facebook.com/UnionIntAvocats/"
                target="_blank"
                rel="noopener noreferrer"
                class="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-sky-400/30 hover:scale-110 transition-all duration-300"
                aria-label="Facebook"
              >
                <font-awesome
                  class="fa-fw text-slate-300 group-hover:text-sky-300 transition-colors"
                  :icon="['fab', 'facebook']"
                />
              </a>
              <a
                href="https://www.youtube.com/channel/UC7dEpI8J2J9Pveku6PjLiTw"
                target="_blank"
                rel="noopener noreferrer"
                class="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-sky-400/30 hover:scale-110 transition-all duration-300"
                aria-label="YouTube"
              >
                <font-awesome
                  class="fa-fw text-slate-300 group-hover:text-sky-300 transition-colors"
                  :icon="['fab', 'youtube']"
                />
              </a>
            </div>
          </div>

          <div class="pt-2">
            <a
              href="https://www.lexisnexis.com/en-us/gateway.page"
              target="_blank"
              rel="noopener noreferrer"
              class="group block p-3 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-white/20 hover:scale-105 transition-all duration-300"
            >
              <picture>
                <source srcset="/logo/logo_lexis.webp" type="image/webp" />
                <img
                  src="/logo/logo_lexis.png"
                  alt="LexisNexis Logo"
                  class="w-full h-auto object-contain opacity-90 group-hover:opacity-100 transition-opacity max-w-[150px]"
                />
              </picture>
            </a>
          </div>
        </div>
        <!-- Newsletter -->
        <div class="lg:col-span-2">
          <CommonNewsletter />
        </div>
      </div>

      <!-- Site Map -->
      <div class="mt-12 pt-8 border-t border-white/10">
        <h4 class="text-base font-semibold text-white mb-6">{{ $t('footer.plan.plan') }}</h4>
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          <div v-for="(section, key) in menuMain" :key="key" class="space-y-2">
            <h5 class="text-sm font-medium text-sky-300">{{ section.title }}</h5>
            <ul class="space-y-1.5">
              <li v-for="(item, index) in section.items.filter((i) => !i.hideInFooter)" :key="index">
                <NuxtLink
                  :to="item.path ? localePath({ name: item.path, params: item.params }) : '#'"
                  class="group inline-flex items-center text-sm text-slate-300 hover:text-sky-300 transition-all duration-300"
                >
                  <font-awesome
                    :icon="['fas', 'chevron-right']"
                    class="text-xs mr-2 transition-transform duration-300 group-hover:translate-x-1 opacity-0 group-hover:opacity-100"
                  />
                  <span>{{ item.title }}</span>
                </NuxtLink>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Bottom Bar -->
      <div class="mt-8 pt-6 border-t border-white/10">
        <div class="flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-slate-400">
          <p>© {{ new Date().getFullYear() }} {{ $t('menu.MenuTop.uia') }}</p>
          <div class="flex gap-4">
            <NuxtLink
              :to="localePath({ name: 'pages-slug', params: { slug: 'conditions-generales-dutilisation' } })"
              class="hover:text-sky-300 transition-colors"
            >
              {{ $t('footer.conditions') }}
            </NuxtLink>
            <NuxtLink
              :to="localePath({ name: 'pages-slug', params: { slug: 'mentions-legales' } })"
              class="hover:text-sky-300 transition-colors"
            >
              {{ $t('footer.legal') }}
            </NuxtLink>
            <NuxtLink
              :to="localePath({ name: 'pages-slug', params: { slug: 'politique-de-protection-des-donnees' } })"
              class="hover:text-sky-300 transition-colors"
            >
              {{ $t('footer.polices') }}
            </NuxtLink>
            <button
              class="hover:text-sky-300 transition-colors cursor-pointer"
              type="button"
              @click="consentStore.openPreferences()"
            >
              {{ $t('consent.manageLink') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped>
  @keyframes blob {
    0%,
    100% {
      transform: translate(0px, 0px) scale(1);
    }
    33% {
      transform: translate(30px, -50px) scale(1.1);
    }
    66% {
      transform: translate(-20px, 20px) scale(0.9);
    }
  }

  .animate-blob {
    animation: blob 7s infinite;
  }

  .animation-delay-2000 {
    animation-delay: 2s;
  }

  .animation-delay-4000 {
    animation-delay: 4s;
  }
</style>
